import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";

import { useSetTitle } from "util/title";
import { profileSlice } from "redux/features/profile";
import { changesSlice, changesSubslice } from "redux/features/changes";
import { RESOURCE_TYPE_PERSON } from "redux/typeConstants";

import {
  Container,
  Message,
  Columns,
  Content,
  Form,
  Button,
} from "react-bulma-components";

import { useReturn } from "util/return";

import ErrorMessage from "util/errorMessage";

import TrimmedInput from "common-frontend/util/trimmedInput";
import SimpleComboBox from "util/simpleComboBox";

import IconButton from "util/iconButton";
import { FaCheck, FaTimes } from "react-icons/fa";

const { Field, Label, Control, Radio, Help } = Form;

export function EditName() {
  useSetTitle("Name");
  const { id, data: profileData } = useSelector(profileSlice.selector);
  const changeRecords = useSelector(changesSlice.selector);
  const nameChangeRecords = changesSubslice(
    changeRecords,
    id,
    "name",
    RESOURCE_TYPE_PERSON,
    id
  );
  const { changes } = nameChangeRecords || { changes: {} };
  const changeKeys = {
    id,
    section: "name",
    resourceType: RESOURCE_TYPE_PERSON,
    resourceId: id,
  };
  const dispatch = useDispatch();

  const [saving, setSaving] = useState(false);

  const setChange = (field, value) => {
    setSaving(false);
    if (value !== profileData[field])
      dispatch(
        changesSlice.actions.setChanged({
          ...changeKeys,
          field,
          value,
        })
      );
    else
      dispatch(
        changesSlice.actions.clearChanged({
          ...changeKeys,
          field,
        })
      );
  };

  const doSave = () => {
    setSaving(true);
    if (nameChangeRecords) dispatch(changesSlice.asyncActions.save(changeKeys));
  };

  const doClose = useReturn();

  useEffect(() => {
    if (saving && !nameChangeRecords) doClose();
  });

  const doCancel = () => {
    dispatch(changesSlice.actions.deleteChangeRecord(changeKeys));
    doClose();
  };

  const saveError = nameChangeRecords ? nameChangeRecords.failed : null;

  const [singleName, setSingleName] = useState(profileData.first_name === "");

  const curValue = Object.fromEntries(
    [
      "honorific",
      "first_name",
      "last_name",
      "surname_is_first_name",
    ].map((field) => [field, changes[field] ?? profileData[field]])
  );
  const clearSingleName = () => {
    setSingleName(false);
    if (curValue.first_name === "")
      setChange("first_name", profileData.first_name);
  };
  const NameSummary = ({ data }) => {
    const firstNameClass = data.surname_is_first_name
      ? "surname"
      : "given-name";
    const lastNameClass = data.surname_is_first_name ? "given-name" : "surname";
    return (
      <span className="name-summary">
        {data.honorific ? data.honorific + " " : null}
        {data.first_name ? (
          <span className={firstNameClass}>{data.first_name + " "}</span>
        ) : null}
        <span className={lastNameClass}>{data.last_name}</span>
      </span>
    );
  };

  const surname =
    curValue.surname_is_first_name && curValue.first_name !== ""
      ? curValue.first_name
      : curValue.last_name;

  const givenName =
    curValue.surname_is_first_name && curValue.first_name !== ""
      ? curValue.last_name
      : curValue.first_name;

  return (
    <Container className="spaced">
      <Message>
        <Message.Header>
          Name <Button remove onClick={doClose} />
        </Message.Header>
        <Message.Body className="basic-edit-name">
          <Columns>
            <Columns.Column size="one-third">
              <Field>
                <Label>Honorific (Mr/Ms/etc)</Label>
                <SimpleComboBox
                  presetValues={[
                    ...(profileData.honorific === null ? [null] : []),
                    "",
                    "Professor",
                    "Dr",
                    "Mr",
                    "Ms",
                    "Mx",
                  ]}
                  presetLabels={{ "": "None" }}
                  value={curValue.honorific}
                  originalValue={profileData.honorific}
                  setValue={(v) => setChange("honorific", v)}
                />
              </Field>
            </Columns.Column>
            {singleName ? null : (
              <Columns.Column size="one-third">
                <Field>
                  <Label>First Name</Label>
                  <Control>
                    <TrimmedInput
                      type="text"
                      value={curValue.first_name}
                      setValue={(v) => setChange("first_name", v)}
                    />
                  </Control>
                  {curValue.first_name === "" ? (
                    <Help color="danger">
                      Please specify your first name or select the "I only have
                      a single name" option below
                    </Help>
                  ) : null}
                </Field>
              </Columns.Column>
            )}
            <Columns.Column size={singleName ? "two-thirds" : "one-third"}>
              <Field>
                <Label>{singleName ? "Name" : "Last Name"} </Label>
                <Control>
                  <TrimmedInput
                    type="text"
                    value={curValue.last_name}
                    setValue={(v) => setChange("last_name", v)}
                  />
                </Control>
              </Field>
            </Columns.Column>
          </Columns>
          <Field>
            <Label>Which one is your surname (family name)?</Label>
            <Control>
              <Radio
                name="rg-surname-order"
                value="last"
                checked={!singleName && !curValue.surname_is_first_name}
                onChange={(e) => {
                  clearSingleName();
                  setChange("surname_is_first_name", false);
                }}
              >
                {" "}
                My surname is my <b>last name</b>
                {singleName ? null : ` (${curValue.last_name})`}, as typical in
                most Western cultures
              </Radio>
            </Control>
            <Control>
              <Radio
                name="rg-surname-order"
                value="first"
                checked={!singleName && curValue.surname_is_first_name}
                onChange={(e) => {
                  clearSingleName();
                  setChange("surname_is_first_name", true);
                }}
              >
                {" "}
                My surname is my <b>first name</b>
                {singleName || !curValue.first_name
                  ? null
                  : ` (${curValue.first_name})`}
                , as typical in some Eastern cultures
              </Radio>
            </Control>
            <Control>
              <Radio
                name="rg-surname-order"
                value="single"
                checked={singleName}
                onChange={(e) => {
                  setSingleName(true);
                  setChange("surname_is_first_name", false);
                  setChange("first_name", "");
                }}
              >
                {" "}
                I only have a single name (not separate given name and surname),
                as in some Indonesian and Icelandic cultures
              </Radio>
            </Control>
            <Help>
              This information helps us address you properly and sort your name
              properly.
            </Help>
          </Field>
          <Message color="success">
            <Message.Body>
              <p>
                We currently have your name as{" "}
                <NameSummary data={profileData} />
              </p>
              {nameChangeRecords ? (
                <p>
                  When you save changes, this will become{" "}
                  <NameSummary data={curValue} />
                </p>
              ) : null}
              <Content>
                This means you {nameChangeRecords ? "will be" : "are"}:
                <ul>
                  <li className="name-usage-example">
                    sorted alphebetically as{" "}
                    <span className="value sort">
                      {curValue.surname_is_first_name
                        ? curValue.first_name + " " + curValue.last_name
                        : curValue.last_name +
                          (curValue.first_name === ""
                            ? ""
                            : ", " + curValue.first_name)}
                    </span>
                  </li>
                  <li className="name-usage-example">
                    referred to formally as{" "}
                    <span className="value">
                      {curValue.honorific
                        ? curValue.honorific + " " + surname
                        : curValue.first_name + " " + curValue.last_name}
                    </span>
                  </li>
                  <li className="name-usage-example">
                    referred to informally as{" "}
                    <span className="value">
                      {givenName === "" ? surname : givenName}
                    </span>
                  </li>
                </ul>
              </Content>
            </Message.Body>
          </Message>
          <Field>
            {saveError ? (
              <ErrorMessage header="Unable to Save Changes">
                {saveError.message}
              </ErrorMessage>
            ) : null}
            {saving && !saveError ? (
              <div className="saving">Saving; please wait</div>
            ) : (
              <div className="buttons">
                <IconButton icon={FaCheck} onClick={doSave}>
                  {nameChangeRecords ? "Save Changes" : "Close"}
                </IconButton>{" "}
                {nameChangeRecords ? (
                  <IconButton icon={FaTimes} onClick={doCancel}>
                    Cancel Changes
                  </IconButton>
                ) : null}
              </div>
            )}
          </Field>
        </Message.Body>
      </Message>
    </Container>
  );
}

export default EditName;
